import type {
  WorkbookCell,
  WorkbookPivotSheet,
  WorkbookResult,
} from '@erp/shared-types';
import { GRAND_TOTAL, PERCENT_FORMAT, resultColumns } from '@erp/workbooks';

/**
 * The Result columns shown as percentages: those the Run computed as
 * shown-as Values (its `columnFormats`), so a Stale Result keeps showing as
 * it was computed. A Result recorded before formats were reads them from
 * the spec.
 */
export function pivotPercentColumns(
  sheet: WorkbookPivotSheet,
  result: WorkbookResult,
): Set<string> {
  const formats =
    result.columnFormats ??
    resultColumns(sheet, result.columns).map(
      (column) => column.numberFormat ?? null,
    );
  return new Set(
    result.columns.filter((_, index) => formats[index] === PERCENT_FORMAT),
  );
}

function isTotalLabel(cell: WorkbookCell | undefined): boolean {
  return (
    typeof cell === 'string' &&
    (cell === GRAND_TOTAL || cell.endsWith(' Total'))
  );
}

/**
 * Whether a Row field cell of a Result row is the blank a spreadsheet leaves
 * beside a subtotal or Grand Total label, rather than an Empty group's label:
 * it is Empty, and an earlier Row field cell of its row reads "… Total"
 * with only Empty cells after it.
 */
export function totalRowBlank(
  rowFieldCount: number,
  row: readonly WorkbookCell[],
  column: number,
): boolean {
  if (column >= rowFieldCount || row[column] !== null) return false;
  for (let label = column - 1; label >= 0; label--) {
    if (row[label] === null) continue;
    return isTotalLabel(row[label]);
  }
  return false;
}
